import { ScheduledClaimExpectedOutcome, ScheduledClaimStatus } from "@/models/ScheduledClaim";

/**
 * Why a fill was dropped into the billing review queue. Mirrors the BillingReviewReason constants
 * the worker persists — the strings, not an enum, because that is what is stored on the fill.
 */
export const BillingReviewReason = {
    ExpectedPaidGotRejected: "Expected Paid, Got Rejected",
    ExpectedRejectionGotPaid: "Expected Rejection, Got Paid",
    RejectCodeMismatch: "Reject Code Mismatch",
    NoPlansPaid: "No Plans Paid",
    ScheduledClaimInvalid: "Scheduled Claim Invalid",
    TransmissionError: "Transmission Error",
    AuditSample: "Audit Sample",
} as const;

/** A flagged fill as it appears in the queue list. */
export class BillingReviewRow {
    storeID: number = 0;
    rxNumber: number = 0;
    rfNumber: number = 0;
    fillDate: string | null = null;
    fillCreated: string | null = null;
    fillCreatedBy: string | null = null;
    patientName: string | null = null;
    drugName: string | null = null;
    // Prescriptions.PatientPay is nullable, so an unpriced fill genuinely has no copay. Typed to say
    // so rather than defaulting to 0, which reads as a real zero-dollar copay.
    copay: number | null = null;
    programName: string | null = null;
    storeName: string | null = null;
    billingReviewReason: string | null = null;

    constructor(source: Partial<BillingReviewRow> | null = null) {
        if (source) Object.assign(this, source);
    }

    get rxId(): string {
        return `${this.storeID}-${this.rxNumber}-${this.rfNumber}`;
    }

    /**
     * Whether the flag is holding the fill out of order creation. Kept in step with
     * BillingReviewGate.NonBlockingReasons: sampling reviews claims that already matched, so a
     * sampled fill ships normally while it waits to be looked at.
     */
    get blocksOrderCreation(): boolean {
        return this.billingReviewReason !== BillingReviewReason.AuditSample;
    }
}

/** A staged claim in the cross-store Scheduled tab. */
export class ScheduledClaimRow {
    id: number | null = null;
    storeID: number = 0;
    rxNumber: number = 0;
    rfNumber: number = 0;
    sequencePosition: number = 0;
    expectedOutcome: ScheduledClaimExpectedOutcome = ScheduledClaimExpectedOutcome.Paid;
    expectedRejectCodes: string | null = null;
    scheduled: string | null = null;
    scheduledBy: string | null = null;
    fillDate: string | null = null;
    patientName: string | null = null;
    drugName: string | null = null;
    /** Null when the plan row carries no name, which is the case for most of them. */
    planName: string | null = null;
    programName: string | null = null;
    storeName: string | null = null;

    constructor(source: Partial<ScheduledClaimRow> | null = null) {
        if (source) Object.assign(this, source);
    }

    get rxId(): string {
        return `${this.storeID}-${this.rxNumber}-${this.rfNumber}`;
    }

    get expectedOutcomeLabel(): string {
        return this.expectedOutcome === ScheduledClaimExpectedOutcome.Rejected ? "Rejected" : "Paid";
    }
}

/** One staged plan beside what the payer actually answered. */
export class BillingReviewDetailItem {
    id: number | null = null;
    sequencePosition: number = 0;
    patientPlanID: number = 0;
    planName: string | null = null;
    expectedOutcome: ScheduledClaimExpectedOutcome = ScheduledClaimExpectedOutcome.Paid;
    expectedRejectCodes: string | null = null;
    status: ScheduledClaimStatus = ScheduledClaimStatus.Pending;
    scheduled: string | null = null;
    scheduledBy: string | null = null;
    processed: string | null = null;
    claimId: number | null = null;
    actualTransactionStatus: string | null = null;
    actualRejectCodes: string | null = null;
    planPaid: number | null = null;
    actualCopay: number | null = null;
    reversed: string | null = null;

    constructor(source: Partial<BillingReviewDetailItem> | null = null) {
        if (source) Object.assign(this, source);
    }

    get expectedOutcomeLabel(): string {
        return this.expectedOutcome === ScheduledClaimExpectedOutcome.Rejected ? "Rejected" : "Paid";
    }

    /** Never transmitted, so there is nothing on the right-hand side to compare against. */
    get hasOutcome(): boolean {
        return !!this.actualTransactionStatus;
    }

    /**
     * Whether this plan is the one that broke the prediction. Only reported for a claim that
     * actually came back: a staged claim the worker never reached did not mismatch, it just never
     * ran, and marking those would point the reviewer at every plan below the real problem.
     */
    get isMismatch(): boolean {
        if (!this.hasOutcome) return false;

        const rejected = (this.actualTransactionStatus ?? "").trim().toLowerCase() === "rejected";
        if (this.expectedOutcome === ScheduledClaimExpectedOutcome.Rejected) {
            return !rejected || !this.rejectCodesSatisfied;
        }

        // Anything other than an outright rejection is treated as satisfying an expectation of
        // payment here. This is the display's approximation of the worker's three-way split, which
        // also flags statuses that are neither — the recorded reason on the fill is authoritative.
        return rejected;
    }

    /**
     * Whether the returned codes cover the pinned ones. Nothing pinned accepts any rejection.
     *
     * Compared case-insensitively to match the worker: ScheduledClaimPolicy.NormalizeCodes builds
     * its set with StringComparer.OrdinalIgnoreCase, so a pinned "mr" against a returned "MR"
     * satisfies the expectation there and never flags the fill. Comparing case-sensitively here
     * would paint a mismatch on a row the worker deliberately let through.
     */
    get rejectCodesSatisfied(): boolean {
        const expected = splitCodes(this.expectedRejectCodes).map(normalizeCode);
        if (expected.length === 0) return true;

        const returned = splitCodes(this.actualRejectCodes).map(normalizeCode);
        return returned.length > 0 && returned.every(code => expected.includes(code));
    }

    get expectedRejectCodeList(): string[] {
        return splitCodes(this.expectedRejectCodes);
    }

    get actualRejectCodeList(): string[] {
        return splitCodes(this.actualRejectCodes);
    }
}

export class BillingReviewHeader {
    storeID: number = 0;
    rxNumber: number = 0;
    rfNumber: number = 0;
    fillDate: string | null = null;
    patientID: number | null = null;
    requiresBillingReview: boolean = false;
    /** Set once somebody has reviewed the fill; null while the review is outstanding. */
    billingReviewId: number | null = null;
    billingReviewReason: string | null = null;
    copay: number | null = null;
    patientName: string | null = null;
    drugName: string | null = null;
    programName: string | null = null;
    storeName: string | null = null;

    constructor(source: Partial<BillingReviewHeader> | null = null) {
        if (source) Object.assign(this, source);
    }

    get rxId(): string {
        return `${this.storeID}-${this.rxNumber}-${this.rfNumber}`;
    }

    get isOutstanding(): boolean {
        return this.requiresBillingReview && this.billingReviewId === null;
    }
}

export class BillingReviewDetail {
    fill: BillingReviewHeader = new BillingReviewHeader();
    claims: BillingReviewDetailItem[] = [];

    constructor(source: any = null) {
        if (!source) return;
        this.fill = new BillingReviewHeader(source.fill);
        this.claims = (source.claims ?? []).map((c: any) => new BillingReviewDetailItem(c));
    }
}

/**
 * Reject codes arrive as raw NCPDP field data on one side and as technician-entered text on the
 * other, so both are trimmed before comparison — the returned codes are padded to the field width.
 */
function splitCodes(codes: string | null): string[] {
    return (codes ?? "")
        .split(",")
        .map(code => code.trim())
        .filter(code => code.length > 0);
}

/**
 * The comparison form of a reject code. toUpperCase rather than toLocaleUpperCase so the result is
 * culture-independent, matching the ordinal comparer the worker uses. Kept off splitCodes itself:
 * the lists it feeds are also rendered, and those should show what was typed.
 */
function normalizeCode(code: string): string {
    return code.toUpperCase();
}
